import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type OrganizationId,
  type OrganizationIdInput,
  type ScopedAdminQueryKey,
} from '@/lib/organization-scoping'
import { queryOptions, skipToken, useQuery } from '@tanstack/react-query'
import { printProductCatalog } from './productCatalogPrint.api'
import type { ProductCatalogPrintInput } from './productCatalogPrint.types'

/** A preview of the catalog the options describe; null while the options are invalid. */
interface ProductCatalogPrintPreviewScope {
  organizationId: OrganizationId
  values: ProductCatalogPrintInput | null
}

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn' | 'enabled'>> & {
  enabled?: boolean
}

export const productCatalogPrintQueryKeys = {
  all: ({ organizationId }: { organizationId?: OrganizationIdInput } = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'product-catalog-print' }, { organizationId }),
  ],
  preview: ({ organizationId, values }: ProductCatalogPrintPreviewScope): ScopedAdminQueryKey =>
    buildScopedAdminQueryKey(productCatalogPrintQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'preview',
      values,
    }),
}

export const productCatalogPrintQueries = {
  preview: ({ organizationId, values }: ProductCatalogPrintPreviewScope) =>
    queryOptions({
      queryKey: productCatalogPrintQueryKeys.preview({ organizationId, values }),
      // Nothing to render until the options are valid.
      queryFn: values ? () => printProductCatalog({ organizationId, values }) : skipToken,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: 'always',
    }),
}

export function useProductCatalogPrintPreview({
  organizationId,
  values,
  enabled = true,
  ...params
}: {
  organizationId?: OrganizationIdInput
  values: ProductCatalogPrintInput | null
} & QueryParams<ReturnType<typeof productCatalogPrintQueries.preview>>) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...productCatalogPrintQueries.preview({
      organizationId: resolvedOrganizationId,
      values,
    }),
    enabled: Boolean(enabled && resolvedOrganizationId),
    placeholderData: (previousData) => previousData,
    ...params,
  })
}
